import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import AreaForm from '../../components/Configuration/AreaForm';
import { getAreas, deleteArea } from '../../services/areaService';

const Areas = ({ breadcrumbMode = 'gestion' }) => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [areas, setAreas] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadAreas = async () => {
    setLoading(true);
    try {
      const res = await getAreas();
      if (res.success && res.data) setAreas(res.data);
      else setToast({ isOpen: true, message: 'Error al cargar áreas', type: 'error' });
    } catch (e) {
      setToast({ isOpen: true, message: e.response?.data?.message || 'Error al cargar áreas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAreas();
  }, []);

  const filtered = areas.filter(a => {
    const matchSearch = !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()) || (a.code && a.code.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchActive = activeFilter === 'all' || (activeFilter === 'active' && a.is_active) || (activeFilter === 'inactive' && !a.is_active);
    return matchSearch && matchActive;
  });

  const handleDeleteClick = (area) => {
    setAreaToDelete(area);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!areaToDelete) return;
    try {
      const res = await deleteArea(areaToDelete.id);
      if (res.success) {
        setAreas(prev => prev.filter(a => a.id !== areaToDelete.id));
        setToast({ isOpen: true, message: 'Área eliminada', type: 'success' });
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al eliminar', type: 'error' });
      }
    } catch (e) {
      setToast({ isOpen: true, message: e.response?.data?.message || 'Error al eliminar', type: 'error' });
    } finally {
      setIsDeleteModalOpen(false);
      setAreaToDelete(null);
    }
  };

  const total = filtered.length;
  const activeCount = filtered.filter(a => a.is_active).length;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Áreas</h1>
              <p className="text-gray-600 mt-1">
                {breadcrumbMode === 'activos'
                  ? 'Áreas de la empresa usadas para ubicar activos (mismo catálogo de Gestión → Configuración).'
                  : 'Gestione las áreas de la empresa'}
              </p>
            </div>
            <button
              onClick={() => { setEditingArea(null); setIsFormOpen(true); }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nueva Área
            </button>
          </div>
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            {breadcrumbMode === 'activos' ? (
              <>
                <Link to="/activos" className="hover:text-teal-600">Activos</Link>
                <span className="mx-2">/</span>
                <span className="text-gray-800">Configuración</span>
              </>
            ) : (
              <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            )}
            <span className="mx-2">/</span>
            <span className="text-gray-800">Áreas</span>
          </nav>
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-wrap gap-2">
            <input
              type="text"
              placeholder="Buscar por código o nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            />
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Áreas</div>
            <div className="text-2xl font-bold text-gray-800">{total}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Áreas Activas</div>
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || activeFilter !== 'all' ? 'No hay resultados con los filtros aplicados' : 'No hay áreas registradas'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((area) => (
                    <tr key={area.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-teal-600">{area.code}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">{area.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{area.description || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${area.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {area.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <button onClick={() => { setEditingArea(area); setIsFormOpen(true); }} className="text-purple-600 hover:text-purple-900 mr-4">Editar</button>
                        <button onClick={() => handleDeleteClick(area)} className="text-red-600 hover:text-red-900">Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <AreaForm isOpen={isFormOpen} onClose={() => { setIsFormOpen(false); setEditingArea(null); }} area={editingArea} onSuccess={loadAreas} />
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setAreaToDelete(null); }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Área"
          message={areaToDelete ? `¿Eliminar el área "${areaToDelete.name}" (${areaToDelete.code})?` : ''}
          confirmText="Eliminar"
          cancelText="Cancelar"
        />
        <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
      </div>
    </div>
  );
};

export default Areas;
